import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

type Block =
  | { type: 'paragraph'; lines: string[] }
  | { type: 'heading'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'ol'; items: string[] };

const UL_RE = /^\s*[-*•]\s+(.*)$/;
const OL_RE = /^\s*\d+[.)]\s+(.*)$/;
const HEADING_RE = /^\s*#{1,6}\s+(.*)$/;
const INLINE_RE = /(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+?\*\*|__[^_]+?__|`[^`]+`|\[\d{1,2}\])/g;
const CITATION_RE = /^\[(\d{1,2})\]$/;

export const HELP_ARTICLE_HREF_PREFIX = 'help:';

export function isInternalHref(href: string): boolean {
  return (href.startsWith('/') && !href.startsWith('//')) || href.startsWith(HELP_ARTICLE_HREF_PREFIX);
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}

function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  source.replace(/\r\n?/g, '\n').split('\n').forEach((line) => {
    if (!line.trim()) {
      flush();
      return;
    }
    const heading = line.match(HEADING_RE);
    if (heading) {
      flush();
      blocks.push({ type: 'heading', text: heading[1] });
      return;
    }
    const ul = line.match(UL_RE);
    if (ul) {
      if (!current || current.type !== 'ul') {
        flush();
        current = { type: 'ul', items: [] };
      }
      current.items.push(ul[1]);
      return;
    }
    const ol = line.match(OL_RE);
    if (ol) {
      if (!current || current.type !== 'ol') {
        flush();
        current = { type: 'ol', items: [] };
      }
      current.items.push(ol[1]);
      return;
    }
    if (current && (current.type === 'ul' || current.type === 'ol') && /^\s{2,}/.test(line)) {
      current.items[current.items.length - 1] += ` ${line.trim()}`;
      return;
    }
    if (!current || current.type !== 'paragraph') {
      flush();
      current = { type: 'paragraph', lines: [] };
    }
    current.lines.push(line);
  });
  flush();
  return blocks;
}

interface InlineProps {
  text: string;
  onNavigate: (href: string) => void;
}

function CitationBadge({ n }: { n: string }) {
  return (
    <Box
      component="span"
      title={`Referência ${n}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 17,
        height: 17,
        px: 0.25,
        ml: 0.2,
        borderRadius: '50%',
        fontSize: '0.62rem',
        fontWeight: 700,
        lineHeight: 1,
        color: 'rgba(71, 85, 105, 0.95)',
        bgcolor: 'rgba(148, 163, 184, 0.28)',
        verticalAlign: 'text-bottom',
      }}
    >
      {n}
    </Box>
  );
}

function renderInline({ text, onNavigate }: InlineProps, keyPrefix = 'i'): React.ReactNode[] {
  const parts = text.split(INLINE_RE).filter((p) => p !== '');
  return parts.map((part, idx) => {
    const key = `${keyPrefix}-${idx}`;
    const citation = part.match(CITATION_RE);
    if (citation) {
      return <CitationBadge key={key} n={citation[1]} />;
    }
    const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
    if (link) {
      const [, label, href] = link;
      if (isInternalHref(href)) {
        return (
          <Link
            key={key}
            href={href}
            underline="hover"
            sx={{ fontWeight: 600, cursor: 'pointer' }}
            onClick={(e: React.MouseEvent) => {
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              onNavigate(href);
            }}
          >
            {label}
          </Link>
        );
      }
      if (isExternalHref(href)) {
        return (
          <Link key={key} href={href} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontWeight: 600 }}>
            {label}
          </Link>
        );
      }
      return <React.Fragment key={key}>{label}</React.Fragment>;
    }
    const bold = part.match(/^\*\*([^*]+?)\*\*$/) || part.match(/^__([^_]+?)__$/);
    if (bold) {
      return (
        <Box component="strong" key={key} sx={{ fontWeight: 700 }}>
          {renderInline({ text: bold[1], onNavigate }, key)}
        </Box>
      );
    }
    const code = part.match(/^`([^`]+)`$/);
    if (code) {
      return (
        <Box
          component="code"
          key={key}
          sx={{ fontFamily: 'monospace', fontSize: '0.92em', px: 0.5, borderRadius: 0.75, bgcolor: 'rgba(15,23,42,0.06)' }}
        >
          {code[1]}
        </Box>
      );
    }
    return <React.Fragment key={key}>{part}</React.Fragment>;
  });
}

interface AssistantMarkdownProps {
  content: string;
  onNavigate: (href: string) => void;
  sx?: SxProps<Theme>;
}

const AssistantMarkdown: React.FC<AssistantMarkdownProps> = ({ content, onNavigate, sx }) => {
  const blocks = React.useMemo(() => parseBlocks(content || ''), [content]);

  return (
    <Box
      sx={{
        fontSize: { xs: '0.75rem', sm: '0.78rem', md: '0.8125rem' },
        lineHeight: { xs: 1.5, md: 1.58 },
        wordBreak: 'break-word',
        '& > *:not(:last-child)': { mb: 1 },
        '& p': { m: 0 },
        ...((sx as object) || {}),
      }}
    >
      {blocks.map((block, bi) => {
        const key = `b-${bi}`;
        if (block.type === 'heading') {
          return (
            <Typography key={key} sx={{ fontWeight: 700, fontSize: 'inherit', color: 'inherit' }}>
              {renderInline({ text: block.text, onNavigate }, key)}
            </Typography>
          );
        }
        if (block.type === 'ul' || block.type === 'ol') {
          return (
            <Box
              key={key}
              component={block.type}
              sx={{
                m: 0,
                pl: 2.35,
                '& li': {
                  mb: block.type === 'ol' ? 0.85 : 0.35,
                  pl: 0.25,
                  '&::marker': { fontWeight: block.type === 'ol' ? 700 : 400 },
                },
              }}
            >
              {block.items.map((item, ii) => (
                <li key={`${key}-${ii}`}>{renderInline({ text: item, onNavigate }, `${key}-${ii}`)}</li>
              ))}
            </Box>
          );
        }
        return (
          <Box key={key} component="p" sx={{ m: 0 }}>
            {block.lines.map((line, li) => (
              <React.Fragment key={`${key}-${li}`}>
                {li > 0 && <br />}
                {renderInline({ text: line, onNavigate }, `${key}-${li}`)}
              </React.Fragment>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

export default AssistantMarkdown;
